import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { ADMIN_REWARDS, REWARD_RULES } from "@/constants/testIds";
import { api, currency, formatApiError, releaseReward, updateReferralCommissioning } from "@/lib/api";

const RULE_FIELDS = [
  "effective_from",
  "customer_residential_fee",
  "ambassador_step1_fee",
  "ambassador_step1_max",
  "ambassador_step2_fee",
  "ambassador_step2_max",
  "ambassador_step3_fee",
  "commercial_band1_max_kw",
  "commercial_band1_fee",
  "commercial_band2_max_kw",
  "commercial_band2_fee",
  "commercial_band3_max_kw",
  "commercial_band3_fee",
  "commercial_band4_fee_ambassador",
  "deal_desk_threshold_kw",
];

const RULE_TEST_IDS = {
  customer_residential_fee: REWARD_RULES.customerResidentialFeeInput,
  ambassador_step1_fee: REWARD_RULES.ambassadorStep1FeeInput,
  ambassador_step1_max: REWARD_RULES.ambassadorStep1MaxInput,
  ambassador_step2_fee: REWARD_RULES.ambassadorStep2FeeInput,
  ambassador_step2_max: REWARD_RULES.ambassadorStep2MaxInput,
  ambassador_step3_fee: REWARD_RULES.ambassadorStep3FeeInput,
  commercial_band1_max_kw: REWARD_RULES.commercialBand1MaxInput,
  commercial_band1_fee: REWARD_RULES.commercialBand1FeeInput,
  commercial_band2_max_kw: REWARD_RULES.commercialBand2MaxInput,
  commercial_band2_fee: REWARD_RULES.commercialBand2FeeInput,
  commercial_band3_max_kw: REWARD_RULES.commercialBand3MaxInput,
  commercial_band3_fee: REWARD_RULES.commercialBand3FeeInput,
  commercial_band4_fee_ambassador: REWARD_RULES.commercialBand4AmbassadorFeeInput,
  deal_desk_threshold_kw: REWARD_RULES.dealDeskThresholdInput,
};

const buildRuleForm = (activeRule) => ({
  ...activeRule,
  effective_from: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
});

const buildRulePayload = (form) => Object.fromEntries(
  RULE_FIELDS.map((field) => [field, form[field]]),
);

const RuleInput = ({ field, label, form, editing, update, money = false, suffix }) => (
  <label className="flex flex-col gap-2 rounded-[14px] border border-black/[.08] bg-[#fcfcfa] p-4 text-[11px] font-semibold text-brand-subtle">
    {label}
    {editing ? (
      <Input
        data-testid={RULE_TEST_IDS[field]}
        type="number"
        min="0"
        value={form[field] ?? ""}
        onChange={(event) => update(field, event.target.value)}
      />
    ) : (
      <strong className="text-[20px] text-brand-ink tabular">
        {money ? currency(form[field]) : form[field]}
        {suffix && <small className="ml-1 text-[11px] text-brand-subtle">{suffix}</small>}
      </strong>
    )}
  </label>
);

const RuleSection = ({ eyebrow, title, description, children }) => (
  <section className="tier-system-block">
    <div className="rule-block-head">
      <div><span>{eyebrow}</span><h3>{title}</h3><p>{description}</p></div>
    </div>
    {children}
  </section>
);

const CustomerRules = (props) => (
  <RuleSection eyebrow="Schedule 1" title="Customer Referral" description="Flat fee for commissioned residential projects">
    <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      <RuleInput {...props} field="customer_residential_fee" label="Residential fee" money suffix="per project" />
    </div>
  </RuleSection>
);

const AmbassadorRules = (props) => (
  <RuleSection eyebrow="Schedule 2" title="Ambassador ladder" description="Position in the trailing 365-day commissioned referral window">
    <div className="mt-5 grid gap-3 lg:grid-cols-3">
      <div className="grid gap-3">
        <RuleInput {...props} field="ambassador_step1_fee" label="Step 1 fee" money />
        <RuleInput {...props} field="ambassador_step1_max" label="Step 1 position max" />
      </div>
      <div className="grid gap-3">
        <RuleInput {...props} field="ambassador_step2_fee" label="Step 2 fee" money />
        <RuleInput {...props} field="ambassador_step2_max" label="Step 2 position max" />
      </div>
      <RuleInput {...props} field="ambassador_step3_fee" label="Step 3 fee" money />
    </div>
  </RuleSection>
);

const COMMERCIAL_BANDS = [
  ["Band 1", "commercial_band1_max_kw", "commercial_band1_fee"],
  ["Band 2", "commercial_band2_max_kw", "commercial_band2_fee"],
  ["Band 3", "commercial_band3_max_kw", "commercial_band3_fee"],
];

const CommercialRules = (props) => (
  <RuleSection eyebrow="Commercial projects" title="Commercial bands" description="Flat project fees by contracted DC capacity">
    <div className="mt-5 grid gap-3">
      {COMMERCIAL_BANDS.map(([label, maxField, feeField]) => (
        <div className="grid gap-3 sm:grid-cols-2" key={label}>
          <RuleInput {...props} field={maxField} label={`${label} maximum capacity`} suffix="kW" />
          <RuleInput {...props} field={feeField} label={`${label} fee`} money />
        </div>
      ))}
      <div className="grid gap-3 sm:grid-cols-2">
        <RuleInput {...props} field="commercial_band4_fee_ambassador" label="Band 4 Ambassador fee" money />
        <RuleInput {...props} field="deal_desk_threshold_kw" label="Deal desk threshold" suffix="kW" />
      </div>
    </div>
  </RuleSection>
);

const ComplianceBlocks = ({ form, editing, update, save }) => (
  <div className="rules-footer-grid">
    <section className="tds-block"><div><p>CA CONTROLLED · READ ONLY</p><h3>TDS treatment</h3></div><div><span>Section</span><b>393(1) - 1006</b></div><div><span>Rate</span><b>2% with PAN · 20% without</b></div><div><span>Annual threshold</span><b>₹20,000 cumulative</b></div></section>
    <section className="save-rule-card"><label>Effective from<Input data-testid={REWARD_RULES.effectiveDateInput} disabled={!editing} type="date" value={form.effective_from || ""} onChange={(event) => update("effective_from", event.target.value)} /></label><Button data-testid={REWARD_RULES.saveButton} disabled={!editing} onClick={save}>Save as new version</Button><span>Members will be notified when messaging is enabled.</span></section>
  </div>
);

const VersionHistory = ({ versions }) => (
  <section className="premium-card version-history">
    <div className="section-head"><div><p className="eyebrow">IMMUTABLE LOG</p><h3>Version history</h3></div></div>
    {versions.map((rule) => <div key={rule.id}><span data-label="Version"><b>{rule.version}</b></span><span data-label="Effective from">{rule.effective_from}</span><span data-label="Status"><span className={`status-pill status-${rule.status}`}>{rule.status}</span></span><span data-label="Created by">{rule.created_by}</span></div>)}
  </section>
);

export const RewardRules = ({ activeRule, versions, reload }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (activeRule) setForm(buildRuleForm(activeRule));
  }, [activeRule]);

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const save = async () => {
    try {
      await api.post("/admin/reward-rules", buildRulePayload(form));
      toast.success("New reward rule version saved");
      setEditing(false);
      reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save rules");
    }
  };

  if (!activeRule) return <section className="premium-card loading-card">Loading active rules...</section>;
  const editorProps = { form, editing, update };
  return (
    <div className="space-y-6" data-testid={REWARD_RULES.editor}>
      <section className="rules-intro"><div><p>INCENTIVE GOVERNANCE</p><h2>Reward Rules</h2><span>Changes create a new immutable version. Historical rewards never change.</span></div><Button data-testid={REWARD_RULES.editButton} variant={editing ? "outline" : "default"} onClick={() => setEditing((current) => !current)}>{editing ? "Cancel editing" : "Edit rules"}</Button></section>
      <CustomerRules {...editorProps} />
      <AmbassadorRules {...editorProps} />
      <CommercialRules {...editorProps} />
      <ComplianceBlocks form={form} editing={editing} update={update} save={save} />
      <VersionHistory versions={versions} />
    </div>
  );
};

const HOLD_REASON_LABELS = {
  deal_desk: "Deal desk review",
  capacity_missing: "Capacity missing",
  schedule_not_launched: "Partner programme not launched",
  attribution_conflict: "Attribution conflict",
};

const FRAUD_REASON_LABELS = {
  self_referral_phone: "Same phone as referrer",
  self_referral_pan: "Same PAN as referrer",
  self_referral_address: "Same address as referrer",
};

const humanize = (value) => value?.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

const SetCapacityDialog = ({ item, reload }) => {
  const [open, setOpen] = useState(false);
  const [capacity, setCapacity] = useState("");
  const [segment, setSegment] = useState("commercial");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await updateReferralCommissioning(item.referral_id, {
        system_capacity_kw: Number(capacity),
        project_segment: segment,
      });
      toast.success("Commissioning details updated");
      setOpen(false);
      await reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update commissioning details");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline">Set capacity</Button></DialogTrigger>
      <DialogContent className="max-w-sm" data-testid={ADMIN_REWARDS.capacityDialog}>
        <DialogHeader><DialogTitle>Set project capacity</DialogTitle><DialogDescription>Add commissioning details to reprice this held reward.</DialogDescription></DialogHeader>
        <label className="grid gap-2 text-sm font-medium">Capacity (kW)<Input data-testid={ADMIN_REWARDS.capacityInput} type="number" min="0.01" step="0.01" value={capacity} onChange={(event) => setCapacity(event.target.value)} /></label>
        <label className="grid gap-2 text-sm font-medium">Project segment<select data-testid={ADMIN_REWARDS.capacitySegmentSelect} className="h-10 rounded-md border border-input bg-transparent px-3 text-sm" value={segment} onChange={(event) => setSegment(event.target.value)}><option value="residential">Residential</option><option value="commercial">Commercial</option></select></label>
        <DialogFooter><Button data-testid={ADMIN_REWARDS.capacitySaveButton} disabled={!capacity || saving} onClick={save}>{saving ? "Saving..." : "Save capacity"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ReleaseRewardDialog = ({ item, reload }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const holdLabel = HOLD_REASON_LABELS[item.hold_reason] || humanize(item.hold_reason);

  const save = async () => {
    const trimmedReason = reason.trim();
    if (trimmedReason.length < 5 || saving) return;
    setSaving(true);
    try {
      await releaseReward(item.id, trimmedReason);
      toast.success("Reward released");
      setReason("");
      setOpen(false);
      await reload();
    } catch (error) {
      toast.error(formatApiError(error, "Could not release reward"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`release-${item.id}`}>Release</Button></DialogTrigger>
      <DialogContent className="max-w-sm" data-testid={`release-dialog-${item.id}`}>
        <DialogHeader><DialogTitle>Release held reward</DialogTitle><DialogDescription>{holdLabel} hold. The amount of {currency(item.net_amount)} is unchanged.</DialogDescription></DialogHeader>
        <label className="grid gap-2 text-sm font-medium" htmlFor={`release-reason-${item.id}`}>Reason<textarea id={`release-reason-${item.id}`} className="min-h-24 rounded-md border border-input bg-transparent px-3 py-2 text-sm" required minLength={5} maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} /></label>
        <DialogFooter><Button data-testid={`release-save-${item.id}`} disabled={reason.trim().length < 5 || saving} onClick={save}>{saving ? "Releasing..." : "Release reward"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RewardOperationList = ({ items, approve, reload }) => (
  <section className="premium-card operations-card">
    <div className="section-head"><div><p className="eyebrow">FINANCE CHECKPOINT</p><h2>Pending reward approvals</h2></div><span className="count-chip">{items.length}</span></div>
    {items.length ? items.map((item) => {
      const held = item.status === "held";
      const reason = HOLD_REASON_LABELS[item.hold_reason] || humanize(item.hold_reason);
      return (
        <div className="operation-row" key={item.id}>
          <div><b>{currency(item.net_amount)}</b><span>{[humanize(item.schedule), humanize(item.band), item.rule_version].filter(Boolean).join(" · ")}</span>{item.supplier_linked === false && <span className="status-pill status-held">No ERP Supplier</span>}{held && <><span className="status-pill status-held">Held</span><span>{reason}</span><small>Approval unavailable while held: {reason}</small></>}</div>
          <div className="flex items-center gap-2">{item.hold_reason === "capacity_missing" && <SetCapacityDialog item={item} reload={reload} />}{held && item.releasable && <ReleaseRewardDialog item={item} reload={reload} />}<Button data-testid={`approve-${item.id}`} disabled={held} onClick={() => approve(item.id)} title={held ? `Approval unavailable while held: ${reason}` : undefined}>Approve</Button></div>
        </div>
      );
    }) : <p className="empty-copy">Approval queue is clear.</p>}
  </section>
);

const FraudList = ({ items, clearFraud }) => (
  <section className="premium-card operations-card">
    <div className="section-head"><div><p className="eyebrow">RISK REVIEW</p><h2>Fraud signals</h2></div><span className="count-chip danger-chip">{items.length}</span></div>
    {items.length ? items.map((item) => <div className="operation-row" key={item.id}><div><b>{item.prospect_name}</b><span>{FRAUD_REASON_LABELS[item.reason] || item.reason}</span></div><Button data-testid={`clear-fraud-${item.id}`} variant="outline" onClick={() => clearFraud(item.id)}>Clear</Button></div>) : <p className="empty-copy">No fraud flags waiting for review.</p>}
  </section>
);

export const AdminOperations = ({ pending, fraud, approve, clearFraud, reload }) => (
  <div className="operations-grid">
    <RewardOperationList items={pending} approve={approve} reload={reload} />
    <FraudList items={fraud} clearFraud={clearFraud} />
  </div>
);
